import { useAppsNavigate } from '@wellesley/router'
import { useCallback, useEffect } from 'react'
import { Application, EntryPoint, User } from '@wellesley/openapi'
import { LastAppProps } from './types'
import { setLastApp, getLastApp } from './lastAppStore'
import { useDefaultChannelLink } from '../useDefaultChannelLink'
import { useDefaultAppLink } from '../useDefaultAppLink'

export const useSetLastApp = ({
  link,
  username,
  groupname,
  isActive,
}: LastAppProps) => {
  useEffect(() => {
    if (isActive) {
      setLastApp({ link, username, groupname })
    }
  }, [isActive, link, username, groupname])
}

type OpenLastAppProps = {
  groupName?: string
  queryParams?: Record<string, string>
  defaultApp?: Application
  defaultChannel?: User
} & (
  | {
      username: string
      groupId?: string
    }
  | {
      username?: string
      groupId: string
    }
)
export const useOpenLastApp = ({ entryPoint }: { entryPoint: EntryPoint }) => {
  const navigate = useAppsNavigate()
  const { getDefaultChannelLink } = useDefaultChannelLink()
  const { getDefaultAppLink } = useDefaultAppLink({ entryPoint })

  const getLastAppLink = useCallback(
    async ({
      username,
      groupId,
      defaultApp,
      groupName,
      defaultChannel,
    }: Omit<OpenLastAppProps, 'queryParams'>) => {
      const lastLink =
        getLastApp({ username, groupname: groupName }) ||
        (groupId &&
          (await getDefaultChannelLink({
            groupId,
            groupName: groupName!,
            defaultChannel,
          }))) ||
        (await getDefaultAppLink({ groupId, groupName, defaultApp }))

      return lastLink
    },
    [getLastApp, getDefaultChannelLink, getDefaultAppLink],
  )
  const openLastApp = useCallback(
    async ({ queryParams, ...props }: OpenLastAppProps) => {
      const lastLink = await getLastAppLink(props)
      if (lastLink) {
        navigate(lastLink, { queryParams })
      }
    },
    [navigate, getLastAppLink],
  )

  return { openLastApp, getLastAppLink }
}
